import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";

import { isNativeMobileApp } from "@/utils/platform.helpers";

interface DeviceInfo {
  model: string;
  os: string;
  osVersion: string;
}

interface DebugContext {
  appVersion: string;
  platform: string;
  deviceInfo: DeviceInfo | null;
  currentRoute: string;
}

/**
 * Builds a debug context object for Intercom bug reports.
 * Gathers app version, platform, device info, and current route.
 */
export async function buildDebugContext(): Promise<DebugContext> {
  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

  // App version: native from Info.plist/manifest, web from env var
  let appVersion = "unknown";
  if (isNativeMobileApp()) {
    try {
      const info = await App.getInfo();
      appVersion = info.version;
    } catch {
      appVersion = "error";
    }
  } else {
    appVersion = import.meta.env.REACT_APP_VERSION || "unknown";
  }

  // Device info (native only)
  let deviceInfo: DeviceInfo | null = null;
  if (isNativeMobileApp()) {
    try {
      const info = await Device.getInfo();
      deviceInfo = {
        model: info.model,
        os: info.operatingSystem,
        osVersion: info.osVersion,
      };
    } catch {
      // Ignore device info errors
    }
  }

  return {
    appVersion,
    platform,
    deviceInfo,
    currentRoute: window.location.pathname,
  };
}
